import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft, ShieldQuestion } from 'lucide-react'
import { useClaims, useResolveClaim } from '../hooks/useProspects'
import type { ProspectClaim } from '../api/prospectsApi'

const STATUSES = ['manual', 'pending', 'verified', 'rejected', 'expired', 'all'] as const

/**
 * The claim review queue.
 *
 * Everything here reached a person for a reason: either we hold no address for the artist
 * (so the automated proof — control of an address we already had — is unavailable), or
 * they are under a publishing takedown, or they had asked us not to contact them. All
 * three are cases where an email click is the wrong thing to accept as proof.
 *
 * Approving TRANSFERS A PERSON'S PAGE and cannot be undone from this screen, so the
 * reviewer is asked for the address they verified rather than being offered a default.
 */
export function ClaimsPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<string>('manual')
  const [active, setActive] = useState<ProspectClaim | null>(null)
  const [email, setEmail] = useState('')
  const [reason, setReason] = useState('')

  const { data, isLoading } = useClaims(status)
  const resolve = useResolveClaim()

  const close = () => { setActive(null); setEmail(''); setReason('') }

  return (
    <div className="space-y-6 p-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/prospects')}>
        <ArrowLeft className="mr-1 h-4 w-4" /> Prospects
      </Button>

      <div>
        <h1 className="text-2xl font-semibold">Claim requests</h1>
        <p className="text-sm text-slate-500">
          Artists asking for the page we built from their work.
        </p>
      </div>

      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm text-amber-900">
            <ShieldQuestion className="h-4 w-4" />
            Why these need a person
          </CardTitle>
          <CardDescription className="text-amber-800">
            A <strong>manual</strong> claim means the automated proof was unavailable — we hold
            no address for them, they are under a takedown, or they had asked us not to write.
            Approving transfers their page and every chapter we drafted. Verify who you are
            talking to first; this cannot be undone here.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="flex flex-wrap gap-1">
        {STATUSES.map((s) => (
          <Button
            key={s}
            variant={status === s ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : !data?.claims.length ? (
            <p className="py-10 text-center text-sm text-slate-500">Nothing waiting.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artist</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>What they said</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.claims.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <button
                        className="text-left font-medium hover:underline"
                        onClick={() => navigate(`/app/prospects/${c.prospect_id}`)}
                      >
                        {c.display_name || c.handle}
                      </button>
                      <div className="text-xs text-slate-500">@{c.handle}</div>
                    </TableCell>
                    <TableCell className="text-sm">{c.status}</TableCell>
                    <TableCell className="max-w-md text-xs text-slate-600">
                      {c.claim_note || <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {new Date(c.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {(c.status === 'manual' || c.status === 'pending') && (
                        <Button size="sm" variant="outline" onClick={() => setActive(c)}>
                          Review
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {active && (
        <Card className="border-slate-300">
          <CardHeader>
            <CardTitle className="text-base">
              Review: {active.display_name || active.handle}
            </CardTitle>
            <CardDescription>
              Approving creates or links an account for the address below and moves the page,
              its chapters and its genome to them.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">
                Verified email — the account this page becomes
              </label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="artist@theirdomain.com"
                className="mt-1 max-w-md"
              />
            </div>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="How did you verify them? (recorded on the audit trail)"
              className="max-w-md"
            />
            <div className="flex gap-2">
              <Button
                disabled={!email.includes('@') || resolve.isPending}
                onClick={() =>
                  resolve.mutate(
                    { claimId: active.id, payload: { decision: 'approve', email, reason } },
                    { onSuccess: close },
                  )
                }
              >
                Approve and transfer
              </Button>
              <Button
                variant="destructive"
                disabled={resolve.isPending}
                onClick={() =>
                  resolve.mutate(
                    { claimId: active.id, payload: { decision: 'reject', reason } },
                    { onSuccess: close },
                  )
                }
              >
                Reject
              </Button>
              <Button variant="ghost" onClick={close}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
